import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { contentService } from '../services/contentService';
import './DocumentPickerModal.css';

/**
 * Simple modal to display and select recent Canvas documents
 */
const DocumentPickerModal = ({ isOpen, onClose, onSelectDocument }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await contentService.fetchCanvasDocuments(20);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDocument = (doc) => {
    onSelectDocument(doc);
    onClose();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getPreviewText = (content) => {
    if (!content) return 'Empty document';
    // Strip HTML tags and get first 100 characters
    const text = content.replace(/<[^>]*>/g, '').trim();
    return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  };

  if (!isOpen) return null;

  return (
    <div className="document-picker-overlay" onClick={onClose}>
      <div className="document-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="document-picker-header">
          <h2>Open Document</h2>
          <button className="document-picker-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="document-picker-content">
          {loading && (
            <div className="document-picker-loading">
              Loading documents...
            </div>
          )}

          {error && (
            <div className="document-picker-error">
              {error}
            </div>
          )}

          {!loading && !error && documents.length === 0 && (
            <div className="document-picker-empty">
              No documents yet. Create your first Canvas document!
            </div>
          )}

          {!loading && !error && documents.length > 0 && (
            <div className="document-picker-list">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  className="document-picker-item"
                  onClick={() => handleSelectDocument(doc)}
                >
                  <div className="document-picker-item-header">
                    <span className="document-picker-item-title">
                      {doc.title || 'Untitled'}
                    </span>
                    <span className="document-picker-item-date">
                      {formatDate(doc.updated_at)}
                    </span>
                  </div>
                  <div className="document-picker-item-preview">
                    {getPreviewText(doc.content)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="document-picker-footer">
          <p className="document-picker-hint">
            Recently modified documents are shown first
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentPickerModal;

